import { Topbar } from "../components/Topbar";
import { KpiCard } from "../components/KpiCard";
import { Panel } from "../components/Panel";
import { DataTable } from "../components/DataTable";
import { LoadingState, ErrorState } from "../components/States";
import { useDashboard } from "../hooks/useDashboard";
import { formatHours, formatNumber } from "../lib/dates";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts";

const COLORS = ["#002FA7", "#3B82F6", "#94A3B8", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#06B6D4"];

export default function Projects() {
  const { data, loading, error } = useDashboard("projects");

  return (
    <>
      <Topbar title="Proyectos" subtitle="Estado, volumen de tareas y dedicación" />
      <div className="p-6 space-y-6" data-testid="projects-page">
        {loading && <LoadingState />}
        {error && <ErrorState message={error} />}
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KpiCard testId="kpi-total-projects" label="Proyectos" value={formatNumber(data.kpis.total_projects, { maximumFractionDigits: 0 })} />
              <KpiCard testId="kpi-active-projects" label="Activos" value={formatNumber(data.kpis.active_projects, { maximumFractionDigits: 0 })} accent="success" />
              <KpiCard testId="kpi-total-tasks" label="Tareas totales" value={formatNumber(data.kpis.total_tasks, { maximumFractionDigits: 0 })} />
              <KpiCard testId="kpi-project-hours" label="Horas en rango" value={formatHours(data.kpis.total_hours)} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Panel title="Distribución por etapa" subtitle="Todas las tareas" testId="chart-project-status">
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.status_distribution}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        innerRadius={50}
                        label={(e) => `${e.name}: ${e.value}`}
                      >
                        {data.status_distribution.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Panel>

              <Panel title="Top proyectos por horas" subtitle="En el rango seleccionado" testId="chart-top-projects">
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.top_projects_hours} layout="vertical" margin={{ left: 100 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} />
                      <YAxis type="category" dataKey="project" width={100} tick={{ fontSize: 10, fill: "#334155" }} />
                      <Tooltip formatter={(v) => formatHours(v)} />
                      <Bar dataKey="hours" fill="#002FA7" radius={[0, 2, 2, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Panel>
            </div>

            <Panel title="Listado de proyectos" subtitle="Primeros 50" testId="table-projects">
              <DataTable
                testId="projects-table"
                filename="proyectos.csv"
                rows={data.projects}
                columns={[
                  { key: "name", label: "Proyecto", accessor: "name" },
                  { key: "customer", label: "Cliente", accessor: "customer" },
                  { key: "manager", label: "Responsable", accessor: "manager" },
                  { key: "tasks", label: "Tareas", accessor: "tasks" },
                  {
                    key: "active", label: "Estado",
                    accessor: (r) => (r.active ? "Activo" : "Archivado"),
                    render: (r) => (
                      <span className={r.active ? "text-emerald-600 font-medium" : "text-slate-400"}>
                        {r.active ? "Activo" : "Archivado"}
                      </span>
                    ),
                  },
                ]}
              />
            </Panel>
          </>
        )}
      </div>
    </>
  );
}
